import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { IoChevronDown, IoMailOutline, IoLockClosedOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import AssignOriginalReportModal from '../pages/Admin/components/AssignOriginalReportModal';
import '../styles/common.scss';
import '../styles/admin.scss'; // Import new admin styles

// Import Images
import RwireLogo from '../assets/images/RW_Logo_SVG.svg';
import SignOutIcon from '../assets/images/Signout.png';
import MarketInsightsIcon from '../assets/images/MarketInsights.png';
import AddWhiteIcon from '../assets/images/addWhite.png';
import DashboardIcon from '../assets/images/Dashboard.png';
import AllReportsIcon from '../assets/images/AllReportsDashboard.png';
import UploadReportIcon from '../assets/images/UploadReport.png';
import CategoriesIcon from '../assets/images/CategoriesDashboard.png';
import UsersIcon from '../assets/images/UsersDashboard.png';
import SettingsIcon from '../assets/images/SettingsDashboard.png';

const AdminLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isAllReportsOpen, setIsAllReportsOpen] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);

    const toggleAllReports = () => setIsAllReportsOpen(!isAllReportsOpen);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const getInitials = (name) => {
        if (!name) return "AD";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <div className="d-flex admin-layout">
            {/* Header */}
            <header className="fixed-top bg-white border-bottom px-4 d-flex align-items-center justify-content-between admin-header">
                <div className="d-flex align-items-center">
                <img
                    src={RwireLogo}
                    alt="Researchwire Logo"
                    className="Footer-logo"
                    width="70"
                    height="40"
                    style={{ objectFit: "contain" }}
                />
                    {/* <img src={RwireLogo} alt="ResearchWire Logo" className="header-logo" /> */}
                </div>

                <div className="d-flex align-items-center gap-4">
                    <div className="d-flex align-items-center gap-2">
                        <div className="user-initials">
                            {getInitials(user?.fullName)}
                        </div>
                        <div className="d-none d-md-block line-height-1">
                            <small className="d-block fw-bold text-dark">{user?.fullName || "Admin User"}</small>
                        </div>
                    </div>

                    <div className="sign-out-btn" onClick={handleLogout}>
                        <img src={SignOutIcon} alt="Sign Out" />
                        <span>Sign out</span>
                    </div>
                </div>
            </header>

            {/* Sidebar */}
            <div className="admin-sidebar d-flex flex-column"
                style={{ width: '260px', minWidth: '260px', marginTop: '70px', height: 'calc(100vh - 70px)', overflowY: 'auto' }}>

                {/* Market Insights Header */}
                <div className="py-2 sidebar-header-section mb-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <img src={MarketInsightsIcon} alt="Market Insights" style={{ height: '24px' }} />
                        <h6 className="m-0 fw-bold commonColor">Market Insights</h6>
                    </div>
                    <small className="text-secondary d-block ps-1" style={{ marginLeft: '32px' }}>Admin Portal</small>
                </div>

                {/* Search Bar Removed */}


                {/* New Report Button - Hidden for Viewers */}
                {!user?.roles?.includes('ROLE_VIEWER') && (
                    <div className="px-3 mb-4 mt-2">
                        <Link to="/admin/reports/upload" className="btn w-100 d-flex align-items-center justify-content-center gap-2" style={{ background: "linear-gradient(0deg, #012970, #024ed6)", color: "#FFFFFF" }}>
                            <img src={AddWhiteIcon} alt="Add" style={{ height: '16px' }} />
                            New Report
                        </Link>
                    </div>
                )}

                {/* Navigation Links */}
                <nav className="flex-grow-1 px-3">
                    <ul className="list-unstyled m-0 d-flex flex-column gap-2">
                        {/* Dashboard */}
                        <li>
                            <Link to="/admin/dashboard"
                                className={`nav-link-custom ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}>
                                <img src={DashboardIcon} alt="Dashboard" style={{ width: '20px' }} />
                                <span>Dashboard</span>
                            </Link>
                        </li>

                        {/* All Reports Dropdown */}
                        <li>
                            <div
                                className={`nav-link-custom cursor-pointer ${location.pathname.includes('/admin/reports') && !location.pathname.includes('upload') ? 'active' : ''}`}
                                onClick={toggleAllReports}
                                style={{ justifyContent: 'space-between' }}
                            >
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    <img src={AllReportsIcon} alt="All Reports" style={{ width: '20px' }} />
                                    <span>All Reports</span>
                                </div>
                                <IoChevronDown style={{ transform: isAllReportsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </div>

                            {/* Submenu */}
                            {isAllReportsOpen && (
                                <ul className="list-unstyled ps-5 mt-2 bg-white rounded container-fluid py-0 basebgcolor">
                                    <li className="mb-2">
                                        <Link to="/admin/reports/published" className={`text-decoration-none d-block small ${location.pathname === '/admin/reports/published' ? 'commonColor fw-bold' : 'text-secondary'}`}>Published</Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/reports/drafts" className={`text-decoration-none d-block small ${location.pathname === '/admin/reports/drafts' ? 'commonColor fw-bold' : 'text-secondary'}`}>Drafts</Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* Upload Report - Hidden for Viewers */}
                        {!user?.roles?.includes('ROLE_VIEWER') && (
                            <li>
                                <Link to="/admin/reports/upload"
                                    className={`nav-link-custom ${location.pathname === '/admin/reports/upload' ? 'active' : ''}`}>
                                    <img src={UploadReportIcon} alt="Upload" style={{ width: '20px' }} />
                                    <span>Upload Report</span>
                                </Link>
                            </li>
                        )}

                        {/* Upload / Assign User Report Files */}
                        {!user?.roles?.includes('ROLE_VIEWER') && (
                            <li>
                                <button
                                    type="button"
                                    onClick={() => setShowAssignModal(true)}
                                    className="nav-link-custom border-0 bg-transparent w-100 text-start text-dark shadow-none p-0 ps-3 py-2 d-flex align-items-center gap-2">
                                    <IoLockClosedOutline style={{ width: '20px', fontSize: '20px' }} />
                                    <span>Upload User Files</span>
                                </button>
                            </li>
                        )}

                        {/* User Documents - Super Admin */}
                        {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                            <li>
                                <Link to="/admin/user-documents"
                                    className={`nav-link-custom ${location.pathname === '/admin/user-documents' ? 'active' : ''}`}>
                                    <IoDocumentTextOutline style={{ width: '20px', fontSize: '20px' }} />
                                    <span>User Documents</span>
                                </Link>
                            </li>
                        )}

                        {/* Categories - Only for Super Admin */}
                        {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                            <li>
                                <Link to="/admin/categories"
                                    className={`nav-link-custom ${location.pathname === '/admin/categories' ? 'active' : ''}`}>
                                    <img src={CategoriesIcon} alt="Categories" style={{ width: '20px' }} />
                                    <span>Categories</span>
                                </Link>
                            </li>
                        )}

                        {/* Inquiries - Only for Super Admin */}
                        {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                            <li>
                                <Link to="/admin/inquiries"
                                    className={`nav-link-custom ${location.pathname === '/admin/inquiries' ? 'active' : ''}`}>
                                    <IoMailOutline style={{ width: '20px', fontSize: '20px' }} />
                                    <span>Inquiries</span>
                                </Link>
                            </li>
                        )}

                        {/* Analytics */}
                        {/* <li>
                            <Link to="/admin/analytics"
                                className={`nav-link-custom ${location.pathname === '/admin/analytics' ? 'active' : ''}`}>
                                <img src={AnalyticsIcon} alt="Analytics" style={{ width: '20px' }} />
                                <span>Analytics</span>
                            </Link>
                        </li> */}

                        {/* Users - Only for Super Admin */}
                        {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                            <li>
                                <Link to="/admin/users"
                                    className={`nav-link-custom ${location.pathname === '/admin/users' ? 'active' : ''}`}>
                                    <img src={UsersIcon} alt="Users" style={{ width: '20px' }} />
                                    <span>Users</span>
                                </Link>
                            </li>
                        )}

                        {/* Settings - Only for Super Admin */}
                        {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                            <li>
                                <Link to="/admin/settings"
                                    className={`nav-link-custom ${location.pathname === '/admin/settings' ? 'active' : ''}`}>
                                    <img src={SettingsIcon} alt="Settings" style={{ width: '20px' }} />
                                    <span>Settings</span>
                                </Link>
                            </li>
                        )}
                    </ul>
                </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex-grow-1 d-flex flex-column" style={{ marginTop: '70px', minWidth: 0 }}>
                {/* Page Content */}
                <div className="flex-grow-1 overflow-hidden">
                    <Outlet />
                </div>
            </div>

            {/* Dedicated Upload/Assign Direct User Report Files Modal */}
            {showAssignModal && (
                <AssignOriginalReportModal
                    show={showAssignModal}
                    onClose={() => setShowAssignModal(false)}
                />
            )}
        </div>
    );
};

export default AdminLayout;
